import { createContext, useContext, useRef, useState } from 'react'
import Modal from '../components/Modal'

const ModalContext = createContext()

export function ModalProvider({ children }) {
  // Holds the resolver for the promise returned by confirm()/alert().
  //
  // Both used to resolve ONLY via onConfirm, but Modal renders the dismiss
  // button for every non-confirm type (and the Cancel button, and Escape)
  // wired to onClose — which never reached onConfirm. So `await alert(...)`
  // and a cancelled `await confirm(...)` both hung forever, silently: no
  // error, just a promise that never settled. Any caller with a
  // try/finally around it left its loading flag stuck on.
  const resolverRef = useRef(null)

  // Settle exactly once; whichever of confirm/close fires first wins.
  const settle = (value) => {
    const resolve = resolverRef.current
    resolverRef.current = null
    if (resolve) resolve(value)
  }

  const [modalState, setModalState] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'confirm',
    onConfirm: () => {},
    confirmText: 'Confirm',
    cancelText: 'Cancel',
  })

  const showModal = ({ title, message, type = 'confirm', onConfirm = () => {}, confirmText = 'Confirm', cancelText = 'Cancel' }) => {
    setModalState({
      isOpen: true,
      title,
      message,
      type,
      onConfirm,
      confirmText,
      cancelText,
    })
  }

  // Resolves true when confirmed, false when cancelled or dismissed.
  const confirm = ({ title, message, onConfirm, confirmText, cancelText }) => {
    return new Promise((resolve) => {
      settle(false) // abandon any previous unsettled modal
      resolverRef.current = resolve
      showModal({
        title,
        message,
        type: 'confirm',
        onConfirm: () => {
          onConfirm?.()
          settle(true)
        },
        confirmText,
        cancelText,
      })
    })
  }

  // Always resolves once the user dismisses it.
  const alert = ({ title, message, type = 'info' }) => {
    return new Promise((resolve) => {
      settle(false)
      resolverRef.current = resolve
      showModal({
        title,
        message,
        type,
        onConfirm: () => settle(true),
      })
    })
  }

  const closeModal = () => {
    setModalState((prev) => ({ ...prev, isOpen: false }))
    // Dismissing (OK button, Cancel, or Escape) must settle the promise too,
    // or every awaiting caller hangs.
    settle(false)
  }

  return (
    <ModalContext.Provider value={{ showModal, confirm, alert, closeModal }}>
      {children}
      <Modal
        isOpen={modalState.isOpen}
        onClose={closeModal}
        onConfirm={modalState.onConfirm}
        title={modalState.title}
        message={modalState.message}
        type={modalState.type}
        confirmText={modalState.confirmText}
        cancelText={modalState.cancelText}
      />
    </ModalContext.Provider>
  )
}

export function useModal() {
  const context = useContext(ModalContext)
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider')
  }
  return context
}
